import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { promotionService } from '../services/promotionService';
import { errorMessage } from '../services/apiError';
import { referralService, ReferralInfo } from '../services/referralService';

import { ShareEarnSection } from './ShareEarnSection';

import './ReferralModal.css';

export type CreditMode = 'refer' | 'share';

interface ReferralModalProps {
  initialMode?: CreditMode;
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
}

const MODE_COPY: Record<CreditMode, { description: string; title: string }> = {
  refer: {
    title: 'Refer & earn',
    description: 'Invite friends with your personal link.',
  },
  share: {
    title: 'Reels & views',
    description: 'Post a project reel, then earn credits from verified views.',
  },
};

const CreditModeIcon: React.FC<{ mode: CreditMode }> = ({ mode }) => (
  <span aria-hidden="true" className="credit-mode-tab-icon">
    {mode === 'refer' ? (
      <svg fill="none" viewBox="0 0 20 20">
        <path d="M7.2 9.1a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
        <path d="M2.5 16.5c.3-3 2-4.6 4.7-4.6s4.4 1.6 4.7 4.6" />
        <path d="M13.3 7.1h4.2M15.4 5v4.2" />
      </svg>
    ) : (
      <svg fill="none" viewBox="0 0 20 20">
        <rect height="12" rx="3" width="15" x="2.5" y="4" />
        <path d="m8.4 7.4 4.4 2.6-4.4 2.6V7.4Z" />
      </svg>
    )}
  </span>
);

const ModeSwitcher: React.FC<{ mode: CreditMode; onChange: (mode: CreditMode) => void }> = ({ mode, onChange }) => (
  <div aria-label="Credit earning method" className="credit-mode-switcher" role="group">
    <button
      aria-pressed={mode === 'refer'}
      className={`credit-mode-tab credit-mode-tab--refer${mode === 'refer' ? ' credit-mode-tab--active' : ''}`}
      onClick={() => onChange('refer')}
      type="button"
    >
      <CreditModeIcon mode="refer" />
      <span className="credit-mode-tab-copy">
        <span className="credit-mode-tab-label">Refer friends</span>
        <span className="credit-mode-tab-meta">5 + 20 credits</span>
      </span>
    </button>
    <button
      aria-pressed={mode === 'share'}
      className={`credit-mode-tab credit-mode-tab--share${mode === 'share' ? ' credit-mode-tab--active' : ''}`}
      onClick={() => onChange('share')}
      type="button"
    >
      <CreditModeIcon mode="share" />
      <span className="credit-mode-tab-copy">
        <span className="credit-mode-tab-label">Share a reel</span>
        <span className="credit-mode-tab-meta">Up to 100 view credits/day</span>
      </span>
    </button>
  </div>
);

interface ReferralProgramProps {
  copied: boolean;
  error: string | null;
  info: ReferralInfo | null;
  onCopy: () => void;
}

const ReferralProgram: React.FC<ReferralProgramProps> = ({ copied, error, info, onCopy }) => (
  <section className="referral-rewards referral-rewards--focused">
    <dl aria-label="Referral summary" className="referral-stats referral-stats--focused">
      <div className="stat-card">
        <dt className="stat-label">Friends referred</dt>
        <dd className="stat-value">{info?.total_referred ?? '-'}</dd>
      </div>
      <div className="stat-card">
        <dt className="stat-label">Credits earned</dt>
        <dd className="stat-value stat-value--amber">{info?.credits_earned ?? '-'}</dd>
      </div>
    </dl>

    <div className="referral-program-hero">
      <span className="referral-program-eyebrow">Your personal invite</span>
      <h3>Give friends a head start.</h3>
      <p>One link tracks their visit, signup, and subscription rewards automatically.</p>
    </div>

    <div className="referral-reward-grid">
      <div className="referral-reward-card">
        <strong>+5</strong>
        <span>When a friend signs up</span>
      </div>
      <div className="referral-reward-card">
        <strong>+20</strong>
        <span>When they subscribe</span>
      </div>
    </div>

    {error && <div className="referral-invite-note" role="alert">{error}</div>}

    <div className="referral-link-section">
      <div className="referral-link-label">Your referral link</div>
      <div className="referral-link-box">
        <span className="referral-link-text">{info?.referral_link || 'Loading…'}</span>
        <button
          className={`referral-copy-btn${copied ? ' copied' : ''}`}
          disabled={!info?.referral_link}
          onClick={onCopy}
          type="button"
        >
          {copied ? '✓ Copied' : 'Copy link'}
        </button>
      </div>
    </div>

  </section>
);

const legacyCopyReferralLink = (link: string): void => {
  const textArea = document.createElement('textarea');

  textArea.value = link;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

const copyReferralLink = async (link: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(link);
  } catch {
    legacyCopyReferralLink(link);
  }
};

interface UseReferralInfoResult {
  error: string | null;
  info: ReferralInfo | null;
}

const useReferralInfo = (active: boolean): UseReferralInfoResult => {
  const [info, setInfo] = useState<ReferralInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchInfo = useCallback(async () => {
    setError(null);
    try {
      const data = await referralService.getReferralInfo();

      setInfo(data);
    } catch (err) {
      console.error('Failed to fetch referral info');
      setError(errorMessage(err, 'We couldn\u2019t load your referral link. Close this window and try again.'));
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    void fetchInfo();
  }, [active, fetchInfo]);

  return { error, info };
};

const useReferralCopy = (link?: string) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!link) return;
    void copyReferralLink(link).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return { copied, handleCopy };
};

interface ReferralModalContentProps {
  initialMode: CreditMode;
  onClose: () => void;
  projectId?: string;
}

const ReferralModalContent: React.FC<ReferralModalContentProps> = ({ initialMode, onClose, projectId }) => {
  const [mode, setMode] = useState<CreditMode>(initialMode);
  const { error, info } = useReferralInfo(mode === 'refer');
  const { copied, handleCopy } = useReferralCopy(info?.referral_link);

  const copy = MODE_COPY[mode];

  useEffect(() => {
    promotionService.prefetchShareInfo();
  }, []);

  return (
    <div className="referral-modal-overlay" onClick={onClose}>
      <div
        aria-labelledby="free-credits-title"
        aria-modal="true"
        className={`referral-modal referral-modal--${mode}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="referral-modal-header">
          <div className="referral-modal-heading">
            <h2 id="free-credits-title">{copy.title}</h2>
            <p>{copy.description}</p>
          </div>
          <button aria-label="Close" className="referral-modal-close" onClick={onClose} type="button">×</button>
        </div>

        <ModeSwitcher mode={mode} onChange={setMode} />

        <div className="referral-modal-body">
          {mode === 'refer' ? (
            <ReferralProgram copied={copied} error={error} info={info} onCopy={handleCopy} />
          ) : (
            <ShareEarnSection projectId={projectId} />
          )}
        </div>


      </div>
    </div>
  );
};

// Portaled to <body> like every other modal here (see BillingModal). The
// sidebar this is rendered from is `position: absolute; z-index: 3` in the
// merged-timeline layout, which makes it a stacking context — left in place,
// the overlay's z-index: 10000 is clamped to that level and body-portaled
// chrome such as the artifact floating toolbar (z-index: 500) paints on top.
export const ReferralModal: React.FC<ReferralModalProps> = ({ initialMode, isOpen, onClose, projectId }) => {
  if (!isOpen) return null;

  return createPortal(
    <ReferralModalContent
      initialMode={initialMode ?? (projectId ? 'share' : 'refer')}
      onClose={onClose}
      projectId={projectId}
    />,
    document.body,
  );
};
